// "Needs you" for the signed-in coach (plan 004): who needs them, and why, with Clear and Undo.
// Used once, in AdminPanel, so the home-screen line and the group at the top of Manage Clients
// show the same people from one load.
//
// Whose clients: a coach's own (users.coach_id); for a super admin, their own and any client with
// no coach at all (sign-up gives a client without a coach code to the super admin; one payment path
// can leave coach_id empty). Loaded on its own, not from Manage Clients' first 100.
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabase';
import { loadSessionsForClients, loadTrainedOpenSessionIds, summariseClientSessions } from '../services/sessionAccountingService';
import { getUnreadForClients, getClientsTheCoachHasWritten } from '../services/messagesService';
import { getCheckInsAwaitingReviewDetail } from '../services/checkInService';
import { getLatestClears, clearAttention, undoClear } from '../services/attentionService';
import { needsYouToday, seenForClear, CHECK_IN_WINDOW_DAYS, NEW_CLIENT_DAYS } from '../utils/needsYouToday';

const EMPTY = { clients: [], stats: {}, unreadMessages: {}, checkInsWaiting: {}, coachHasWritten: {}, clears: {} };

export function useNeedsYou({ enabled = true } = {}) {
  const [input, setInput] = useState(EMPTY);
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);
  const loadId = useRef(0);

  // quiet: refresh without the "checking" state (coming back to the list after reading messages)
  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!enabled) return;
    const mine = ++loadId.current;
    if (!quiet) setLoading(true);
    setFailed(false);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not signed in');
      const { data: me, error: meError } = await supabase
        .from('users').select('auth_user_id, role').eq('auth_user_id', user.id).single();
      if (meError) throw meError;

      let query = supabase
        .from('users')
        .select('id, auth_user_id, name, status, subscription_status, is_complimentary, created_at, coach_id')
        .eq('role', 'client');
      query = me.role === 'super_admin'
        ? query.or(`coach_id.eq.${me.auth_user_id},coach_id.is.null`)
        : query.eq('coach_id', me.auth_user_id);
      const { data: clients, error: clientsError } = await query.order('name');
      if (clientsError) throw clientsError;
      const list = (clients || []).filter((c) => c.auth_user_id);
      const ids = list.map((c) => c.auth_user_id);
      if (ids.length === 0) {
        if (mine === loadId.current) setInput(EMPTY);
        return;
      }

      const joinedSince = Date.now() - (NEW_CLIENT_DAYS + 1) * 86400000;
      const newIds = list.filter((c) => c.created_at && new Date(c.created_at).getTime() >= joinedSince).map((c) => c.auth_user_id);
      const [sessions, flagsRes, unreadMessages, checkInsWaiting, coachHasWritten, clears] = await Promise.all([
        loadSessionsForClients(ids),
        supabase.from('exercise_flags').select('id, user_id, flagged_at').in('user_id', ids).is('resolved_at', null),
        getUnreadForClients(ids),
        getCheckInsAwaitingReviewDetail(ids, CHECK_IN_WINDOW_DAYS),
        getClientsTheCoachHasWritten(newIds),
        getLatestClears(ids),
      ]);
      if (flagsRes.error) throw flagsRes.error;
      const trainedOpenIds = await loadTrainedOpenSessionIds(sessions);
      const now = new Date();
      const stats = {};
      for (const uid of ids) {
        const flags = (flagsRes.data || []).filter((f) => f.user_id === uid);
        stats[uid] = {
          ...summariseClientSessions(sessions.filter((s) => s.user_id === uid), trainedOpenIds, now),
          flagCount: flags.length,
          newestFlagAt: flags.reduce((n, f) => (f.flagged_at && (!n || f.flagged_at > n) ? f.flagged_at : n), null),
        };
      }
      if (mine === loadId.current) setInput({ clients: list, stats, unreadMessages, checkInsWaiting, coachHasWritten, clears });
    } catch (err) {
      console.error('[useNeedsYou] load error:', err);
      if (mine === loadId.current) setFailed(true);
    } finally {
      if (mine === loadId.current) setLoading(false);
    }
  }, [enabled]);

  useEffect(() => { load(); }, [load]);

  const rows = needsYouToday(input);

  // Returns the saved Clear (for Undo). Throws if it was not saved; the card then stays.
  const clear = useCallback(async (uid) => {
    const saved = await clearAttention(uid, seenForClear({ uid, ...input }));
    setInput((prev) => ({ ...prev, clears: { ...prev.clears, [uid]: saved } }));
    return saved;
  }, [input]);

  // Undo: removes that Clear, then reads the newest Clear again (someone else's may be newer).
  const undo = useCallback(async (saved) => {
    await undoClear(saved.id);
    const latest = await getLatestClears([saved.client_id]);
    setInput((prev) => {
      const clears = { ...prev.clears };
      if (latest[saved.client_id]) clears[saved.client_id] = latest[saved.client_id];
      else delete clears[saved.client_id];
      return { ...prev, clears };
    });
  }, []);

  return { rows, loading, failed, reload: load, clear, undo };
}
